import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFileUpload } from 'shared-ui';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { API_BASE_URL, BUSINESS_ID } from '../config/api';
import { enablePushNotifications, isPushSupported } from '../utils/pushNotifications';

function priceForQuantity(tiers, quantity) {
  const sorted = [...tiers].sort((a, b) => a.minQty - b.minQty);
  let unitPrice = sorted[0]?.price ?? 0;
  for (const tier of sorted) {
    if (quantity >= tier.minQty) unitPrice = tier.price;
  }
  return unitPrice;
}

export default function PrintOrderIntake() {
  const { customer, isAuthenticated, getAuthHeaders } = useCustomerAuth();
  const { upload, uploading, error: uploadError } = useFileUpload({
    apiBaseUrl: API_BASE_URL,
    tokenKey: 'customer_token',
  });

  const [tiers, setTiers] = useState([]);
  const [pricingError, setPricingError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('pickup');
  const [notes, setNotes] = useState('');
  const [designFile, setDesignFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [pushState, setPushState] = useState('idle'); // idle | enabling | enabled | error
  const [pushError, setPushError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE_URL}/print-orders/pricing/${BUSINESS_ID}`)
      .then((res) => {
        if (!res.ok) throw new Error('Pricing is not available right now');
        return res.json();
      })
      .then((data) => setTiers(data.pricing?.tiers || []))
      .catch((err) => setPricingError(err.message || 'Pricing is not available right now'));
  }, []);

  useEffect(() => {
    if (isAuthenticated && customer) {
      setCustomerName(`${customer.firstName || ''} ${customer.lastName || ''}`.trim());
      setCustomerEmail(customer.email || '');
      setCustomerPhone(customer.phone || '');
    }
  }, [isAuthenticated, customer]);

  const unitPrice = useMemo(() => priceForQuantity(tiers, quantity), [tiers, quantity]);
  const total = useMemo(() => Math.round(unitPrice * quantity * 100) / 100, [unitPrice, quantity]);

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const confirmed = await upload(file, { fileType: 'DESIGN_ASSET' });
        setDesignFile(confirmed);
      } catch {
        // upload() already sets its own `error` state, surfaced below
      }
    },
    [upload]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!customerName.trim() || !customerEmail.trim()) {
      setSubmitError('Name and email are required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/print-orders/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessId: BUSINESS_ID,
          quantity,
          customerName,
          customerEmail,
          customerPhone: customerPhone || undefined,
          deliveryMode,
          notes: notes || undefined,
          designFileIds: designFile ? [designFile.id] : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not submit your order');
      setConfirmedOrder(data.order);
    } catch (err) {
      setSubmitError(err.message || 'Could not submit your order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnablePush = async () => {
    setPushState('enabling');
    setPushError('');
    try {
      await enablePushNotifications(getAuthHeaders());
      setPushState('enabled');
    } catch (err) {
      setPushState('error');
      setPushError(err.message || 'Could not enable notifications');
    }
  };

  if (confirmedOrder) {
    return (
      <div style={{ maxWidth: 560, margin: '60px auto', padding: '0 20px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 28 }}>🎉 Order received</h1>
        <p style={{ color: '#555', fontSize: 16 }}>
          Order <strong>{confirmedOrder.orderNumber}</strong> is in. We'll let you know right here in your account
          when it's ready {deliveryMode === 'pickup' ? 'for pickup' : 'to ship'}.
        </p>

        {isAuthenticated && isPushSupported() && pushState !== 'enabled' && (
          <div style={{ marginTop: 24, padding: 20, background: '#f4fbfa', borderRadius: 10 }}>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: '#333' }}>
              Want a push straight to your phone the moment it's ready, instead of checking back?
            </p>
            <button
              type="button"
              onClick={handleEnablePush}
              disabled={pushState === 'enabling'}
              style={{
                padding: '10px 20px',
                background: '#0e9c8d',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                cursor: pushState === 'enabling' ? 'not-allowed' : 'pointer',
              }}
            >
              {pushState === 'enabling' ? 'Enabling…' : '🔔 Notify my phone'}
            </button>
            {pushError && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{pushError}</div>}
          </div>
        )}
        {pushState === 'enabled' && (
          <p style={{ marginTop: 20, color: '#0e9c8d', fontSize: 14 }}>✓ You'll get a push when it's ready.</p>
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 560, margin: '40px auto', padding: '0 20px' }}>
      <h1 style={{ fontSize: 28, marginBottom: 4 }}>Start a custom print order</h1>
      <p style={{ color: '#666', marginBottom: 28 }}>
        Tell us what you need — price drops automatically as the quantity goes up.
      </p>

      {pricingError && <div style={{ color: '#b91c1c', marginBottom: 16 }}>{pricingError}</div>}

      <form onSubmit={handleSubmit}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Quantity</label>
        <input
          type="number"
          min={1}
          value={quantity}
          onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
          style={{ width: '100%', padding: 10, marginBottom: 8, border: '1px solid #ddd', borderRadius: 6 }}
        />

        {tiers.length > 0 && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            {[...tiers]
              .sort((a, b) => a.minQty - b.minQty)
              .map((t) => (
                <div
                  key={t.minQty}
                  style={{
                    flex: 1,
                    minWidth: 100,
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: quantity >= t.minQty ? '2px solid #0e9c8d' : '1px solid #e5e5e5',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 11, color: '#888' }}>{t.minQty}+</div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>${t.price}</div>
                </div>
              ))}
          </div>
        )}

        <div style={{ marginBottom: 20, fontSize: 15 }}>
          Estimated total: <strong>${total.toFixed(2)}</strong> (${unitPrice}/ea)
        </div>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Your name</label>
        <input
          type="text"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          required
          style={{ width: '100%', padding: 10, marginBottom: 16, border: '1px solid #ddd', borderRadius: 6 }}
        />

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Email</label>
        <input
          type="email"
          value={customerEmail}
          onChange={(e) => setCustomerEmail(e.target.value)}
          required
          style={{ width: '100%', padding: 10, marginBottom: 16, border: '1px solid #ddd', borderRadius: 6 }}
        />

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Phone (optional)</label>
        <input
          type="tel"
          value={customerPhone}
          onChange={(e) => setCustomerPhone(e.target.value)}
          style={{ width: '100%', padding: 10, marginBottom: 16, border: '1px solid #ddd', borderRadius: 6 }}
        />

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Pickup or ship?</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {['pickup', 'ship'].map((mode) => (
            <button
              type="button"
              key={mode}
              onClick={() => setDeliveryMode(mode)}
              style={{
                flex: 1,
                padding: 10,
                borderRadius: 6,
                border: deliveryMode === mode ? '2px solid #0e9c8d' : '1px solid #ddd',
                background: deliveryMode === mode ? '#e6faf7' : 'white',
                cursor: 'pointer',
              }}
            >
              {mode === 'pickup' ? '🏬 Pickup' : '📦 Ship'}
            </button>
          ))}
        </div>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Your design</label>
        {isAuthenticated ? (
          <>
            <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" onChange={handleFileChange} disabled={uploading} />
            {uploading && <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>Uploading…</div>}
            {uploadError && <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 4 }}>{uploadError}</div>}
            {designFile && (
              <div style={{ fontSize: 13, color: '#0e9c8d', marginTop: 4 }}>✓ {designFile.filename} uploaded</div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>
            Log in to attach a design file directly — or just describe it in the notes below and we'll follow up.
          </div>
        )}

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginTop: 16, marginBottom: 6 }}>
          Notes (design details, sizes, colors, deadline…)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          style={{ width: '100%', padding: 10, marginBottom: 20, border: '1px solid #ddd', borderRadius: 6 }}
        />

        {submitError && <div style={{ color: '#b91c1c', marginBottom: 16 }}>{submitError}</div>}

        <button
          type="submit"
          disabled={submitting || tiers.length === 0}
          style={{
            width: '100%',
            padding: 14,
            background: '#0e9c8d',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 700,
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Submitting…' : 'Submit order'}
        </button>
      </form>
    </div>
  );
}
